// Central SEO + AIO (answer-engine) configuration for Bharat Ethanol Forum 2026.
import { EVENT } from "@/data/site";

export const SITE_URL = "https://bharatethanolforum.com";
export const OG_IMAGE = `${SITE_URL}/assets/site/og-image.jpg`;
export const SITE_NAME = "Bharat Ethanol Forum 2026";

const abs = (path) => `${SITE_URL}${path === "/" ? "" : path}`;

// Per-route metadata. Keep titles < 60 chars, descriptions < 160 where possible.
export const SEO_MAP = {
  "/": {
    title: "Bharat Ethanol Forum 2026 | India's Ethanol & Biofuel Conference + Expo",
    description:
      "India's Ethanol Economy & Biofuel Leadership Forum — 28–29 Sept 2026, Mumbai. A B2B conference + tech expo on E20/E30 blending, flex-fuel, 2G, CBG and SAF. Register now.",
    keywords: "Bharat Ethanol Forum, ethanol conference India, biofuel expo Mumbai, E20 E30 blending, flex-fuel, SAF, 2G ethanol, ethanol summit 2026",
  },
  "/about": {
    title: "About | Bharat Ethanol Forum 2026, Mumbai",
    description:
      "Why the Bharat Ethanol Forum exists: uniting producers, oil companies, automakers, technology providers, investors and policymakers around India's post-E20 biofuel roadmap.",
  },
  "/conference": {
    title: "Conference | Bharat Ethanol Forum 2026",
    description:
      "Two days of market outlook, policy direction and dealmaking — five business sessions, a CEO roundtable and the India Ethanol Vision 2030 Declaration, 28–29 Sept 2026, Mumbai.",
  },
  "/conference/agenda": {
    title: "Agenda | Bharat Ethanol Forum 2026",
    description:
      "The two-day programme: India's ethanol economy, feedstock, flex-fuel & OMC demand, global markets, SAF & CBG, the leadership awards and the Vision 2030 Declaration.",
  },
  "/conference/speakers": {
    title: "Speakers | Bharat Ethanol Forum 2026",
    description:
      "Leaders from across India's ethanol and biofuel value chain. Speaker line-up for the inaugural Bharat Ethanol Forum, 28–29 Sept 2026, Mumbai. Propose a speaker.",
  },
  "/expo": {
    title: "Expo | Ethanol & Biofuels Trade & Tech Expo 2026, Mumbai",
    description:
      "India's biofuel technology marketplace — plant equipment, process tech, automation and EPC, in front of producers, project developers and investors. Reserve a stand.",
  },
  "/attend": {
    title: "Attend | Bharat Ethanol Forum 2026 Delegate Passes",
    description:
      "Who attends and why: delegate passes, the audience profile and what you'll take away from India's leading ethanol conference + expo. 28–29 Sept 2026, Mumbai.",
  },
  "/venue": {
    title: "Venue & Travel | Westin Powai Lake, Mumbai",
    description:
      "Getting to Westin Powai Lake, Mumbai, where to stay and how to plan your trip for the Bharat Ethanol Forum 2026, 28–29 September 2026.",
  },
  "/sponsor": {
    title: "Sponsor | Bharat Ethanol Forum 2026",
    description:
      "Sponsorship tiers, deliverables and the rate card for the Bharat Ethanol Forum 2026. Put your brand in front of India's ethanol and biofuel decision-makers.",
  },
  "/exhibit": {
    title: "Exhibit | Ethanol & Biofuels Trade & Tech Expo 2026",
    description:
      "Stall options, sizes and rates for exhibiting at India's ethanol & biofuels expo. Showcase plant technology, equipment and services to buyers with budgets.",
  },
  "/awards": {
    title: "Awards | Bharat Ethanol Leadership Awards 2026",
    description:
      "Ten categories recognising leadership across India's ethanol economy — producers, technology, investment and sustainability. Submit a nomination.",
  },
  "/ethanol-impact-calculator": {
    title: "Ethanol Impact Calculator | Crude, Rupees & CO₂ Saved",
    description:
      "Free interactive tool: see what ethanol blending is worth in crude displaced, rupees saved and CO₂ avoided — built on government-reported figures.",
    keywords: "ethanol calculator, ethanol blending savings, crude displacement, CO2 saved ethanol, E20 E30 impact",
  },
  "/insights": {
    title: "Insights | India Ethanol & Biofuel Knowledge Hub",
    description:
      "Explainers and analysis on India's ethanol economy — blending, the E20-to-E30 roadmap, flex-fuel, 2G ethanol and sustainable aviation fuel.",
  },
  "/register": {
    title: "Register | Bharat Ethanol Forum 2026, Mumbai",
    description:
      "Secure your delegate pass for the Bharat Ethanol Forum 2026. Fast checkout with live GST and Early-Bird pricing for Indian and international delegates.",
  },
  "/contact": {
    title: "Contact | Bharat Ethanol Forum 2026",
    description:
      "Talk to the team about delegate, sponsor, exhibitor, speaker or press queries for the Bharat Ethanol Forum 2026, Mumbai.",
  },
  "/privacy": { title: "Privacy Policy | Bharat Ethanol Forum 2026", description: "How the Bharat Ethanol Forum handles your data.", noindex: true },
  "/terms": { title: "Terms & Conditions | Bharat Ethanol Forum 2026", description: "Terms of use, registration, payments and conditions for the Bharat Ethanol Forum 2026, Mumbai." },
  "/refund-policy": { title: "Cancellation & Refund Policy | Bharat Ethanol Forum 2026", description: "Delegate cancellation charges, refund timelines and substitution terms for the Bharat Ethanol Forum 2026. Refunds processed via Razorpay." },
};

export function getSeo(pathname) {
  const clean = pathname.replace(/\/$/, "") || "/";
  return SEO_MAP[clean] || null;
}

// Topic landing-page slugs (rendered by TopicPage with their own <Seo/>).
export const TOPIC_BREADCRUMB = {
  "/ethanol-india": "Ethanol in India",
  "/ethanol-market-india": "Ethanol Market",
  "/ethanol-prices-india": "Ethanol Prices",
  "/ethanol-policy-india": "Ethanol Policy",
  "/sugar-ethanol-india": "Sugar Ethanol",
  "/grain-ethanol-india": "Grain Ethanol",
  "/maize-ethanol-india": "Maize Ethanol",
  "/biofuels-india": "Biofuels in India",
  "/sustainable-aviation-fuel-india": "Sustainable Aviation Fuel",
  "/flex-fuel-vehicles-india": "Flex-Fuel Vehicles",
};

// ---------- JSON-LD builders (AIO) ----------

export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "Tefla's",
    url: SITE_URL,
    logo: `${SITE_URL}/assets/bharat-ethanol-forum-logo.png`,
    email: EVENT.email,
    telephone: EVENT.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: "301 AB & 308, Abhishek Premises, C-5 Dalia Industrial Estate, Off New Link Road, Andheri (W)",
      addressLocality: "Mumbai",
      postalCode: "400053",
      addressRegion: "Maharashtra",
      addressCountry: "IN",
    },
  };
}

export function websiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-IN",
  };
}

export function eventLd() {
  return {
    "@context": "https://schema.org",
    "@type": "BusinessEvent",
    "@id": `${SITE_URL}/#event`,
    name: "Bharat Ethanol Forum 2026",
    alternateName: "Ethanol & Biofuels Trade & Tech Expo 2026",
    description:
      "India's Ethanol Economy & Biofuel Leadership Forum — a two-day B2B conference and concurrent technology expo covering E20/E30 blending, flex-fuel, 2G ethanol, compressed biogas and sustainable aviation fuel.",
    startDate: "2026-09-28T09:00:00+05:30",
    endDate: "2026-09-29T18:00:00+05:30",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: [OG_IMAGE],
    url: SITE_URL,
    inLanguage: "en-IN",
    keywords: "ethanol, biofuel, E20, E30, flex-fuel, 2G ethanol, SAF, compressed biogas, India",
    location: {
      "@type": "Place",
      name: "Westin Powai Lake, Mumbai",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Mumbai",
        addressRegion: "Maharashtra",
        addressCountry: "IN",
      },
    },
    organizer: { "@type": "Organization", name: "Tefla's", url: SITE_URL },
    offers: {
      "@type": "Offer",
      name: "Delegate Pass",
      url: `${SITE_URL}/register`,
      price: "15000",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      validFrom: "2026-06-01T00:00:00+05:30",
    },
  };
}

export function breadcrumbLd(pathname) {
  const clean = pathname.replace(/\/$/, "") || "/";
  const items = [{ name: "Home", path: "/" }];
  if (clean !== "/") {
    const parts = clean.split("/").filter(Boolean);
    let acc = "";
    parts.forEach((p) => {
      acc += `/${p}`;
      const meta = SEO_MAP[acc];
      const name = meta ? meta.title.split("|")[0].trim() : (TOPIC_BREADCRUMB[acc] || p.replace(/-/g, " "));
      items.push({ name, path: acc });
    });
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.path),
    })),
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleLd(article, slug) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.dek,
    image: [article.image],
    inLanguage: "en-IN",
    author: { "@type": "Organization", name: "Bharat Ethanol Forum" },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: abs(`/insights/${slug}`),
    about: "India ethanol economy and biofuel policy",
  };
}

// Forum-level FAQ for the home page (strong AIO signal).
export const HOME_FAQS = [
  { q: "What is the Bharat Ethanol Forum 2026?", a: "The Bharat Ethanol Forum 2026 is India's Ethanol Economy & Biofuel Leadership Forum — a two-day B2B conference and concurrent technology expo held on 28–29 September 2026 at Westin Powai Lake, Mumbai, organised by Tefla's." },
  { q: "When and where is the Bharat Ethanol Forum 2026?", a: "It takes place on 28–29 September 2026 at Westin Powai Lake, Mumbai, India." },
  { q: "Who should attend?", a: "Ethanol and sugar producers, grain processors, oil marketing companies, flex-fuel automakers, distillery technology and EPC firms, investors and funds, policymakers and global biofuel companies." },
  { q: "What topics does the conference cover?", a: "India's post-E20 growth, the E30 roadmap, feedstock (sugar, grain, maize, rice and 2G), flex-fuel vehicles and OMC demand, global ethanol trade, and adjacencies including compressed biogas (CBG) and sustainable aviation fuel (SAF)." },
  { q: "How do I register or exhibit?", a: "Delegates can register online with live GST and Early-Bird pricing. Companies can sponsor or reserve an exhibition stall through the Sponsor and Exhibit pages, or download the brochure and rate card." },
];
